/**
 * The daily send limits: how many notification emails one person may receive
 * in a day (any company, every route), and how much one site may send one
 * supplier in a day.
 *
 * The number counts **sends, not deliveries**. One summary reaching ten people
 * at the same supplier spends one, because the whole fan-out shares a batch.
 * That is the only framing under which "10 a day" means anything to an admin,
 * and the copy says it out loud — a limit whose unit the reader has to guess is
 * a limit they will set wrongly.
 *
 * Two levels, matching the backend's resolution order: a default for every
 * supplier, and exceptions for the ones that need their own. Nobody should have
 * to fill in a number per supplier, so the default carries the weight and the
 * exception list stays short.
 *
 * The default lives on the settings row and saves through the tab's existing
 * `onChange`, exactly like quiet hours. Exceptions live on the relationship and
 * have their own endpoint — see {@link useSendBudgets}.
 */
import { useEffect, useState } from 'react';
import type { NotificationSettingsUpdate } from '../../../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { AlertCircle, Gauge, Loader2, MailWarning, X } from 'lucide-react';
import type { MasterSwitches } from './types';
import { useSendBudgets } from './useSendBudgets';

interface NotificationSendBudgetCardProps {
  master: MasterSwitches;
  canConfigure: boolean;
  saving: boolean;
  onChange: (patch: NotificationSettingsUpdate, message: string) => void;
  /** Resolved company id, echoed on writes so a re-scoped screen 409s. */
  company?: number;
}

/** `''` while the field is being cleared; anything non-numeric is refused. */
const asCount = (text: string): number | null => {
  if (!/^\d{0,5}$/.test(text)) return null;
  return text === '' ? 0 : Number(text);
};

/**
 * "today" beats a date an admin has to compare against a calendar, and it is
 * the answer to the question they are actually asking. Falls back to the raw
 * `YYYY-MM-DD` rather than guessing a locale format.
 */
const describeDay = (isoDate: string): string => {
  if (!isoDate) return '';
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const asLocalIso = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (isoDate === asLocalIso(today)) return 'today';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (isoDate === asLocalIso(yesterday)) return 'yesterday';
  return isoDate;
};

export function NotificationSendBudgetCard({
  master,
  canConfigure,
  saving,
  onChange,
  company,
}: NotificationSendBudgetCardProps) {
  const budgets = useSendBudgets(company);
  const [draftDefault, setDraftDefault] = useState(String(master.defaultMaxEmailsPerDay));
  const [pickedSupplier, setPickedSupplier] = useState('');
  const [draftException, setDraftException] = useState('');
  const [draftPerPerson, setDraftPerPerson] = useState(String(master.maxEmailsPerUserPerDay));

  // Re-sync when the server row changes underneath us (load, or another
  // save). A rejected write leaves the draft in place so it can be retried.
  useEffect(() => {
    setDraftDefault(String(master.defaultMaxEmailsPerDay));
  }, [master.defaultMaxEmailsPerDay]);
  useEffect(() => {
    setDraftPerPerson(String(master.maxEmailsPerUserPerDay));
  }, [master.maxEmailsPerUserPerDay]);

  const disabled = !canConfigure || saving;
  const parsedDefault = asCount(draftDefault);
  const defaultChanged =
    parsedDefault !== null && parsedDefault !== master.defaultMaxEmailsPerDay;

  const parsedPerPerson = asCount(draftPerPerson);
  const perPersonChanged =
    parsedPerPerson !== null && parsedPerPerson !== master.maxEmailsPerUserPerDay;

  const applyPerPerson = () => {
    if (parsedPerPerson === null) return;
    onChange(
      { max_emails_per_user_per_day: parsedPerPerson },
      parsedPerPerson > 0
        ? `Limit set to ${parsedPerPerson} emails per person a day`
        : 'Per-person limit removed',
    );
  };

  const exceptions = budgets.suppliers.filter((row) => row.maxEmailsPerDay > 0);
  const available = budgets.suppliers.filter((row) => row.maxEmailsPerDay === 0);
  const parsedException = asCount(draftException);
  const canAddException =
    !disabled
    && !budgets.saving
    && pickedSupplier !== ''
    && parsedException !== null
    && parsedException > 0;

  const applyDefault = () => {
    if (parsedDefault === null) return;
    onChange(
      { default_max_emails_per_day: parsedDefault },
      parsedDefault > 0
        ? `Default limit set to ${parsedDefault} a day`
        : 'Default limit removed',
    );
  };

  const addException = async () => {
    if (parsedException === null) return;
    await budgets.save(Number(pickedSupplier), parsedException);
    setPickedSupplier('');
    setDraftException('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5" />
          Daily send limit
        </CardTitle>
        <CardDescription>
          How much notification email one person, and one supplier, may receive in a day.
          For a supplier, one send counts once however many people it reaches, so a summary going to
          ten contacts spends one. Digests and daily summaries are never limited.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="send-budget-per-person" className="text-sm">
                Limit per person, per day
              </Label>
              <Input
                id="send-budget-per-person"
                inputMode="numeric"
                className="w-36"
                value={draftPerPerson}
                disabled={disabled}
                onChange={(e) => {
                  if (asCount(e.target.value) !== null) setDraftPerPerson(e.target.value);
                }}
              />
            </div>
            <Button
              variant="outline"
              disabled={disabled || !perPersonChanged}
              onClick={applyPerPerson}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply limit
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {master.maxEmailsPerUserPerDay > 0
              ? `Nobody, customer or supplier, is emailed more than ${master.maxEmailsPerUserPerDay} notifications in any 24 hours. Past that the in-app bell still fires.`
              : 'No per-person limit — each person receives as many emails as their notifications produce.'}
          </p>
        </div>

        <div className="space-y-3 border-t pt-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              {/*
                "per supplier" is load-bearing: the first reading of a number
                sitting under one heading is a shared pool, and this is not one.
                Each supplier gets this many of its own.
              */}
              <Label htmlFor="send-budget-default" className="text-sm">
                Default limit, per supplier
              </Label>
              <Input
                id="send-budget-default"
                inputMode="numeric"
                className="w-36"
                value={draftDefault}
                disabled={disabled}
                onChange={(e) => {
                  if (asCount(e.target.value) !== null) setDraftDefault(e.target.value);
                }}
              />
            </div>
            <Button
              variant="outline"
              disabled={disabled || !defaultChanged}
              onClick={applyDefault}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply default
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {master.defaultMaxEmailsPerDay > 0
              ? `Each supplier may receive ${master.defaultMaxEmailsPerDay} a day — not ${master.defaultMaxEmailsPerDay} shared between them. Anything past that is held back; the in-app bell still fires.`
              : 'No default limit — suppliers without one of their own receive as much as the events produce.'}
          </p>
        </div>

        <div className="space-y-3 border-t pt-5">
          <div className="space-y-1">
            <Label className="font-medium">Exceptions</Label>
            <p className="text-sm text-muted-foreground">
              Give one supplier its own limit. Everyone else keeps the default.
            </p>
          </div>

          {budgets.loading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading suppliers…
            </p>
          ) : budgets.error ? (
            <p className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {budgets.error}
            </p>
          ) : budgets.suppliers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This site has no active supplier relationships yet, so there is nothing
              to make an exception for.
            </p>
          ) : (
            <>
              {exceptions.length > 0 && (
                <ul className="space-y-2">
                  {exceptions.map((row) => (
                    <li
                      key={row.supplier}
                      className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
                    >
                      <span className="text-sm font-medium">{row.supplierName}</span>
                      <span className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">
                          {row.maxEmailsPerDay} a day
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove the limit for ${row.supplierName}`}
                          disabled={disabled || budgets.saving}
                          onClick={() => void budgets.save(row.supplier, 0)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="send-budget-supplier" className="text-sm">
                    Supplier
                  </Label>
                  <Select
                    value={pickedSupplier}
                    disabled={disabled || budgets.saving || available.length === 0}
                    onValueChange={setPickedSupplier}
                  >
                    <SelectTrigger
                      id="send-budget-supplier"
                      className="w-[240px]"
                      aria-label="Supplier"
                    >
                      <SelectValue
                        placeholder={
                          available.length === 0
                            ? 'Every supplier has one'
                            : 'Choose a supplier…'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {available.map((row) => (
                        <SelectItem key={row.supplier} value={String(row.supplier)}>
                          {row.supplierName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="send-budget-exception" className="text-sm">
                    Limit
                  </Label>
                  <Input
                    id="send-budget-exception"
                    inputMode="numeric"
                    className="w-28"
                    placeholder="e.g. 10"
                    value={draftException}
                    disabled={disabled || budgets.saving}
                    onChange={(e) => {
                      if (asCount(e.target.value) !== null) {
                        setDraftException(e.target.value);
                      }
                    }}
                  />
                </div>
                <Button
                  variant="outline"
                  disabled={!canAddException}
                  onClick={() => void addException()}
                >
                  {budgets.saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Add exception
                </Button>
              </div>
            </>
          )}
        </div>

        {/*
          Only rendered when a limit actually held something back. This is the
          one gate whose outcome cannot be worked out from the settings — it
          depends on how much traffic there happened to be earlier in the day —
          so "why didn't they get it?" needs an answer on the screen rather
          than in the database.
        */}
        {budgets.withheld.length > 0 && (
          <div className="space-y-3 border-t pt-5">
            <div className="space-y-1">
              <Label className="flex items-center gap-1.5 font-medium">
                <MailWarning className="h-4 w-4" />
                Withheld recently
              </Label>
              <p className="text-sm text-muted-foreground">
                Email the supplier limits held back over the last 7 days. The in-app bell
                still fired for every one of them.
              </p>
            </div>
            <ul className="space-y-2">
              {budgets.withheld.map((row) => (
                <li
                  key={row.supplier}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md border px-3 py-2"
                >
                  <span className="text-sm font-medium">{row.supplierName}</span>
                  <span className="text-sm text-muted-foreground">
                    {row.sends} send{row.sends === 1 ? '' : 's'} withheld
                    {row.recipients > 0 && (
                      <> · {row.recipients} recipient{row.recipients === 1 ? '' : 's'}</>
                    )}
                    {row.lastDate && <> · {describeDay(row.lastDate)}</>}
                    {row.cap > 0 && <> · limit {row.cap} a day</>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
